import { GraphData, Product, StreamStage, Capability, Team, AppGroup, FlowEdge, PercentageMetrics } from '../types';

import { KeyUtils } from '../keyUtils';

/**
 * Extracts percentage metrics from API data or generates random values as fallback
 * @param apiData - The API data object that may contain percentage metrics
 * @returns PercentageMetrics instance with either API data or random values
 */
function extractPercentageMetrics(apiData: any): PercentageMetrics {
  // Try to extract percentage data from API if available
  const timePercentage = apiData?.metrics?.timePercentage ?? apiData?.timePercentage;
  const volumePercentage = apiData?.metrics?.volumePercentage ?? apiData?.volumePercentage;
  const qualityPercentage = apiData?.metrics?.qualityPercentage ?? apiData?.qualityPercentage;
  const sentimentPercentage = apiData?.metrics?.sentimentPercentage ?? apiData?.sentimentPercentage;
  const costPercentage = apiData?.metrics?.costPercentage ?? apiData?.costPercentage;
  const riskPercentage = apiData?.metrics?.riskPercentage ?? apiData?.riskPercentage;

  return new PercentageMetrics(
    timePercentage ?? 0,
    volumePercentage ?? 0,
    qualityPercentage ?? 0,
    sentimentPercentage ?? 0,
    costPercentage ?? 0,
    riskPercentage ?? 0
  );
}

export function transformApiToGraphData(apiJson: any, transitionsData: any = null): GraphData {
  // 1. Extract stages, sorted
  const stages: StreamStage[] = (apiJson.stages?.items ?? [])
    .sort((a: any, b: any) => a.treeSortOrder - b.treeSortOrder)
    .map((stage: any) => {
      const percentage = stage.percentage || 0;
      return {
        Name: stage.label,
        Description: stage.comment || '',
        Percentage: percentage,
        // Create PercentageMetrics with default values based on the main percentage
        PercentageMetrics: new PercentageMetrics(percentage, percentage, percentage, percentage, percentage, percentage)
      };
    });

  // 2. Extract capabilities (deduplicate by label+stage, same capability in different stages = separate instances)
  const capabilityMap = new Map<string, Capability>();
  apiJson.lanes?.forEach((lane: any) => {
    lane.stages?.forEach((stage: any) => {
      stage.items?.forEach((item: any) => {
        item.instanceOf?.forEach((cap: any) => {
          const capKey = KeyUtils.forCapabilityGroup(cap.label, stage.label);
          if (!capabilityMap.has(capKey)) {
            // Extract assessedBy IDs from the capability data
            const assessedByIds = item.data?.assessedBy?.map((metric: any) => metric.id) || [];

            capabilityMap.set(capKey, {
              Name: cap.label,
              Stage: stage.label,
              Description: '', // No description in API
              AssessedBy: assessedByIds // Extract IDs from assessedBy field
            })
          } else if (item.data?.assessedBy?.length > 0) {
            // If capability exists in this stage but we found more assessedBy metrics, add them
            const existingCapability = capabilityMap.get(capKey);
            const newAssessedByIds = item.data.assessedBy.map((metric: any) => metric.id);

            if (existingCapability) {
              // Add new IDs without duplicates
              const existingIds = new Set(existingCapability.AssessedBy);
              newAssessedByIds.forEach((id: string) => existingIds.add(id));
              existingCapability.AssessedBy = Array.from(existingIds);
            }
          }
        });
      });
    });
  });
  const capabilities: Capability[] = Array.from(capabilityMap.values());

  // 3. Extract products (from addressedBy in each lane's stage items)
  const productsMap = new Map<string, Product>();
  apiJson.lanes?.forEach((lane: any) => {
    lane.stages?.forEach((stage: any) => {
      stage.items?.forEach((item: any) => {
        item.data?.addressedBy?.forEach((prod: any) => {
          if (!productsMap.has(prod.label)) {
            productsMap.set(prod.label, {
              Name: prod.label,
              Description: '', // No description in API
              Revenue: (Math.floor(Math.random() * 30) + 1).toString() // Dummy revenue
            });
          }
        });
      });
    });
  });
  const products: Product[] = Array.from(productsMap.values());

  // 4. Build team-to-territory map from transitions data
  const teamTerritoryMap = new Map<string, string>();
  if (transitionsData?.data) {
    transitionsData.data.forEach((transition: any) => {
      const teamLabel = transition.OrgUnit?.label;
      const territory = transition.Territory?.Value;
      if (teamLabel && territory && !teamTerritoryMap.has(teamLabel)) {
        teamTerritoryMap.set(teamLabel, territory);
      }
    });
  }

  // 6. Extract Teams, Apps, Mappings, and Flows
  const teamsMap = new Map<string, { Name: string; Description: string }>();
  const applicationGroupsMap = new Map<string, { Name: string; Description: string }>();
  const appGroupPercentagesMap = new Map<string, PercentageMetrics>(); // Track app group percentages
  const teams: Team[] = [];
  const appGroups: AppGroup[] = [];
  const flowEdges: FlowEdge[] = [];
  const leafNodes = new Map<
    string,
    {
      label: string;
      application: string;
      stage: string;
      team: string;
      capability: string;
      products: Set<string>;
      type: string;
      percentage: PercentageMetrics;
    }
  >(); // Track unique leaf nodes with their metadata

  apiJson.lanes?.forEach((lane: any) => {
    lane.stages?.forEach((stage: any) => {
      stage.items?.forEach((item: any) => {
        const capabilityLabel = item.instanceOf?.[0]?.label;
        if (!capabilityLabel) return;

        // --- Handle Teams, Apps, and Mappings ---
        const teamLabels = item.data?.delegatedToOrganisation?.map((org: any) => org.label) ?? [];
        const appLabels = item.data?.deliveredByApplication?.map((app: any) => app.label) ?? [];

        // Process Teams and their mapping to Capabilities
        teamLabels.forEach((teamLabel: string) => {
          if (!teamsMap.has(teamLabel)) {
            teamsMap.set(teamLabel, { Name: teamLabel, Description: '' });
          }
          teams.push({
            team: teamLabel,
            capability: capabilityLabel,
            stage: stage.label, // Include stage information in the mapping
            Territory: teamTerritoryMap.get(teamLabel) ?? '',
            Percentage: new PercentageMetrics()
          });
        });

        // Process Application Groups
        appLabels.forEach((appLabel: string) => {
          if (!applicationGroupsMap.has(appLabel)) {
            applicationGroupsMap.set(appLabel, { Name: appLabel, Description: '' });
          }
        });

        // Map Applications to all Teams within the same capability instance
        appLabels.forEach((appLabel: string) => {
          teamLabels.forEach((teamLabel: string) => {
            // Generate unique key for this app-team-capability-stage combination
            const appGroupKey = `${appLabel}-${teamLabel}-${capabilityLabel}-${stage.label}`;

            if (!appGroupPercentagesMap.has(appGroupKey)) {
              appGroupPercentagesMap.set(appGroupKey, new PercentageMetrics());
            }

            appGroups.push({
              ApplicationGroup: appLabel,
              Team: teamLabel,
              Capability: capabilityLabel,
              Stage: stage.label,
              Percentage: appGroupPercentagesMap.get(appGroupKey)
            });
          });
        });

        // --- Handle Flows ---
        const productLabel = item.data?.addressedBy?.[0]?.label;
        const stageLabel = stage.label;
        const activities = item.data?.enactedBy ?? [];
        const outcomes = item.data?.capabilityDesiredOutcome ?? [];
        const flowAppLabels = item.data?.deliveredByApplication?.map((app: any) => app.label) ?? [];
        // We already have capabilityLabel from earlier in the code

        if (flowAppLabels.length > 0 && productLabel && stageLabel) {
          // For each app and team combination, create flows
          flowAppLabels.forEach((appLabel: string) => {
            // Get all team labels (organizations) for this item
            const currentTeamLabels =
              item.data?.delegatedToOrganisation?.map((org: any) => org.label) ?? [];

            if (activities.length > 0 && outcomes.length > 0) {
              activities.forEach((activity: any) => {
                // Create a leaf node for each team/organization
                currentTeamLabels.forEach((teamLabel: string) => {
                  const leafKey = KeyUtils.forLeafNode(
                    activity.label,
                    appLabel,
                    teamLabel,
                    capabilityLabel,
                    stage.label
                  );

                  if (!leafNodes.has(leafKey)) {
                    leafNodes.set(leafKey, {
                      label: activity.label,
                      application: appLabel,
                      stage: stage.label,
                      team: teamLabel,
                      capability: capabilityLabel,
                      products: new Set(productLabel ? [productLabel] : []),
                      type: 'Activity',
                      percentage: extractPercentageMetrics(activity)
                      //percentage: new PercentageMetrics(100, 100, 100, 100, 100, 100)
                    });
                  } else if (productLabel) {
                    // If leaf node exists, just add the product
                    leafNodes.get(leafKey)?.products.add(productLabel);
                  }
                });

                outcomes.forEach((outcome: any) => {
                  // Create a leaf node for each team/organization
                  currentTeamLabels.forEach((teamLabel: string) => {
                    const leafKey = KeyUtils.forLeafNode(
                      outcome.label,
                      appLabel,
                      teamLabel,
                      capabilityLabel,
                      stage.label
                    );

                    if (!leafNodes.has(leafKey)) {
                      leafNodes.set(leafKey, {
                        label: outcome.label,
                        application: appLabel,
                        stage: stage.label,
                        team: teamLabel,
                        capability: capabilityLabel,
                        products: new Set(productLabel ? [productLabel] : []),
                        type: 'Outcome',
                        percentage: extractPercentageMetrics(outcome)
                        //percentage: new PercentageMetrics(20, 20, 20, 20, 20, 20)
                      });
                    } else if (productLabel) {
                      // If leaf node exists, just add the product
                      leafNodes.get(leafKey)?.products.add(productLabel);
                    }
                  });


                });
              });
            } 
          });
        }
      });
    });
  });

  // Convert application groups map to TeamMapping array
  const applicationGroupMappings: AppGroup[] = [];

  // First, collect all unique application groups with their team mappings
  applicationGroupsMap.forEach((appGroup: any, appLabel: string) => {
    // For each application group, find all teams it's mapped to
    const teamMappings = appGroups
      .filter((mapping) => mapping.ApplicationGroup === appLabel)
      .map((mapping) => ({
        ApplicationGroup: appLabel,
        Team: mapping.Team,
        Capability: mapping.Capability || '',
        Stage: mapping.Stage || '',
        Percentage: mapping.Percentage
      }));

    // If no team mappings found, add a default one
    if (teamMappings.length === 0) {
      applicationGroupMappings.push({
        ApplicationGroup: appLabel,
        Team: '', // Empty string as default team
        Capability: '',
        Stage: '',
        Percentage: new PercentageMetrics()
      });
    } else {
      applicationGroupMappings.push(...teamMappings);
    }
  });

  // Deduplicate mappings
  const unique = (arr: any[]) => [
    ...new Map(arr.map((item) => [JSON.stringify(item), item])).values()
  ];

  // Convert leaf nodes map to LeafNode array
  const leafNodesArray = Array.from(leafNodes.values()).map((node) => ({
    Leaf: node.label,
    Application: node.application,
    Stage: node.stage,
    Team: node.team,
    Capability: node.capability,
    Products: Array.from(node.products) as string[],
    Type: node.type,
    Percentage: node.percentage
  }));
  
  // Process transitions data to create flow edges if available
  if (transitionsData && transitionsData.data && transitionsData.data.length > 0) {
    
    // Create map for direct label lookup
    const leafNodesByLabel = new Map<string, any>();
    
    // Index leaf nodes for efficient lookup
    leafNodesArray.forEach(node => {
      // Index by label for direct lookup
      leafNodesByLabel.set(node.Leaf, node);
    });
    
    // Build a map from task label → team (OrgUnit) for target team resolution
    const taskToTeam = new Map<string, string>();
    transitionsData.data.forEach((transition: any) => {
      const teamLabel = transition.OrgUnit?.label;
      const taskLabel = transition.Task?.label;
      if (teamLabel && taskLabel) {
        taskToTeam.set(taskLabel, teamLabel);
      }
    });

    // Process transitions to create flow edges
    transitionsData.data.forEach((transition: any) => {
      // Extract source task and target outcome from transition
      const sourceTask = transition.Task?.label || '';
      const targetOutcome = transition.Outcome?.label || '';
      const nextTask = transition.NextTask?.label || '';
      const transitionTeam = transition.OrgUnit?.label || '';

      // Create flow edges from Task to Outcome (same team)
      if (sourceTask && targetOutcome) {
        const sourceNode = leafNodesByLabel.get(sourceTask);
        const targetNode = leafNodesByLabel.get(targetOutcome);

        if (sourceNode && targetNode) {
          flowEdges.push({
            SourceLeaf: sourceTask,
            TargetLeaf: targetOutcome,
            SourceTeam: transitionTeam,
            TargetTeam: transitionTeam,
          });
        }
      }

      // Create flow edges from Outcome to NextTask if available (may cross teams)
      if (targetOutcome && nextTask) {
        const sourceNode = leafNodesByLabel.get(targetOutcome);
        const targetNode = leafNodesByLabel.get(nextTask);

        if (sourceNode && targetNode) {
          flowEdges.push({
            SourceLeaf: targetOutcome,
            TargetLeaf: nextTask,
            SourceTeam: transitionTeam,
            TargetTeam: taskToTeam.get(nextTask) || '',
          });
        }
      }
    });
  }

  const result: GraphData = {
    products,
    stages,
    capabilities,
    applicationGroups: unique(applicationGroupMappings) as AppGroup[],
    teams: unique(teams) as Team[],
    flows: unique(flowEdges) as FlowEdge[],
    leafs: leafNodesArray
  };

  return result;
}
